import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { pacientesApi } from '../../services/api';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import type { Paciente } from '../../types/api';

// Función helper para evaluar si un paciente está activo
const isPatientActive = (estado: any): boolean => {
  if (typeof estado === 'boolean') return estado;
  if (typeof estado === 'string') {
    return estado.toLowerCase() === 'activo' || estado.toLowerCase() === 'true' || estado === '1';
  }
  if (typeof estado === 'number') return estado === 1;
  return false;
};

// Componente Switch para el estado
function StatusSwitch({ 
  isActive, 
  onToggle, 
  loading = false 
}: { 
  isActive: boolean; 
  onToggle: () => void; 
  loading?: boolean; 
}) {
  return (
    <button
      onClick={onToggle}
      disabled={loading}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${isActive ? 'bg-green-600' : 'bg-red-600'}
        ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
      `}
    >
      <span
        className={`
          inline-block h-4 w-4 transform rounded-full bg-white transition-transform
          ${isActive ? 'translate-x-6' : 'translate-x-1'}
        `}
      />
    </button>
  );
}

export function PatientsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingPatientId, setEditingPatientId] = useState<number | null>(null);
  const [editingPatient, setEditingPatient] = useState<any>(null);
  const [newPatient, setNewPatient] = useState({
    nombre: '',
    apellidos: '',
    telefono: '',
    email: '',
    fecha_nacimiento: '',
    genero: '',
    estado: 'activo',
    fecha_alta: new Date().toISOString().split('T')[0],
    consentimiento_rgpd: false,
    fuente_captacion: '',
    observaciones: '',
    metodo_contacto_preferido: ''
  });

  // Filtros y paginación
  const [inputFilters, setInputFilters] = useState({
    nombre: '',
    apellidos: '',
    estado: '',
    fecha_inicio: '',
    fecha_fin: ''
  });
  const [filters, setFilters] = useState(inputFilters);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const debounceRef = useRef<any>({});

  // Obtener lista de pacientes con filtros y paginación
  const { data, isLoading } = useQuery<any, Error>({
    queryKey: ['patients', filters, page, limit],
    queryFn: () => pacientesApi.getAll({ ...filters, page, limit }),
    staleTime: 1000 * 60, // 1 minuto
    // keepPreviousData solo es válido en React Query v3, en v4 se usa como opción directa
    // Si usas v4, puedes poner keepPreviousData: true aquí
  });
  const patients: Paciente[] = data?.data || [];
  const total: number = data?.total || 0;

  // Mutación para crear paciente
  const createMutation = useMutation({
    mutationFn: pacientesApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      toast.success('Paciente creado exitosamente');
      setIsAdding(false);
      setNewPatient({
        nombre: '',
        apellidos: '',
        telefono: '',
        email: '',
        fecha_nacimiento: '',
        genero: '',
        estado: 'activo',
        fecha_alta: new Date().toISOString().split('T')[0],
        consentimiento_rgpd: false,
        fuente_captacion: '',
        observaciones: '',
        metodo_contacto_preferido: ''
      });
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar paciente
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      return pacientesApi.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      toast.success('Paciente actualizado exitosamente');
      setEditingPatientId(null);
      setEditingPatient(null);
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  // Mutación para actualizar estado del paciente
  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, estado }: { id: number; estado: string }) => {
      return pacientesApi.update(id, { estado });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patients'] });
      toast.success('Estado del paciente actualizado');
    },
    onError: () => {
      toast.error(t('common.error'));
    },
  });

  const handleToggleStatus = (patient: Paciente) => {
    const currentStatus = isPatientActive(patient.estado);
    const newStatus = currentStatus ? 'inactivo' : 'activo';
    updateStatusMutation.mutate({
      id: patient.id,
      estado: newStatus
    });
  };

  const handleAddNew = () => {
    setIsAdding(true);
  };

  const handleEdit = (patient: Paciente) => {
    setEditingPatientId(patient.id);
    setEditingPatient({
      nombre: patient.nombre || '',
      apellidos: patient.apellidos || patient.apellido || '',
      telefono: patient.telefono || '',
      email: patient.email || '',
      fecha_nacimiento: patient.fecha_nacimiento || '',
      genero: patient.genero || '',
      estado: patient.estado || 'activo',
      fecha_alta: patient.fecha_alta || '',
      consentimiento_rgpd: patient.consentimiento_rgpd || false,
      fuente_captacion: patient.fuente_captacion || '',
      observaciones: patient.observaciones || '',
      metodo_contacto_preferido: patient.metodo_contacto_preferido || ''
    });
  };

  const handleSaveNew = () => {
    if (!newPatient.nombre || !newPatient.apellidos || !newPatient.email) {
      toast.error('Por favor, completa los campos obligatorios: Nombre, Apellidos y Email');
      return;
    }
    createMutation.mutate(newPatient as any);
  };

  const handleSaveEdit = () => {
    if (!editingPatient.nombre || !editingPatient.apellidos || !editingPatient.email) {
      toast.error('Por favor, completa los campos obligatorios: Nombre, Apellidos y Email');
      return;
    }
    updateMutation.mutate({
      id: editingPatientId!,
      data: editingPatient
    });
  };

  const handleCancelNew = () => {
    setIsAdding(false);
    setNewPatient({
      nombre: '',
      apellidos: '',
      telefono: '',
      email: '',
      fecha_nacimiento: '',
      genero: '',
      estado: 'activo',
      fecha_alta: new Date().toISOString().split('T')[0],
      consentimiento_rgpd: false,
      fuente_captacion: '',
      observaciones: '',
      metodo_contacto_preferido: ''
    });
  };

  const handleCancelEdit = () => {
    setEditingPatientId(null);
    setEditingPatient(null);
  };

  const handleInputChange = (field: string, value: any) => {
    setNewPatient(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditInputChange = (field: string, value: any) => {
    setEditingPatient((prev: any) => ({
      ...prev,
      [field]: value
    }));
  };

  // Función para formatear fechas
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('es-ES');
    } catch {
      return dateString;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" text={t('patients.loadingPatients')} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
            {t('patients.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Gestiona la información de los pacientes de la clínica
          </p>
        </div>
        <button
          onClick={handleAddNew}
          disabled={isAdding || editingPatientId !== null}
          className="btn-primary disabled:opacity-50"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          {t('patients.addPatient')}
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-4 items-end bg-gray-50 dark:bg-gray-800 p-4 rounded-lg mb-2">
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Nombre</label>
          <input type="text" value={inputFilters.nombre} onChange={e => setInputFilters(f => ({ ...f, nombre: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Apellidos</label>
          <input type="text" value={inputFilters.apellidos} onChange={e => setInputFilters(f => ({ ...f, apellidos: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-400" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Estado</label>
          <select value={inputFilters.estado} onChange={e => setInputFilters(f => ({ ...f, estado: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100">
            <option value="">Todos</option>
            <option value="ACTIVO">ACTIVO</option>
            <option value="INACTIVO">INACTIVO</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta desde</label>
          <input type="date" value={inputFilters.fecha_inicio} onChange={e => setInputFilters(f => ({ ...f, fecha_inicio: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 dark:text-gray-200">Fecha alta hasta</label>
          <input type="date" value={inputFilters.fecha_fin} onChange={e => setInputFilters(f => ({ ...f, fecha_fin: e.target.value }))} className="input input-sm bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500 text-gray-900 dark:text-gray-100" />
        </div>
        <div>
          <button onClick={() => { setFilters(inputFilters); setPage(1); }} className="btn btn-sm bg-blue-600 hover:bg-blue-700 text-white">Buscar</button>
          <button onClick={() => { setInputFilters({ nombre: '', apellidos: '', estado: '', fecha_inicio: '', fecha_fin: '' }); setFilters({ nombre: '', apellidos: '', estado: '', fecha_inicio: '', fecha_fin: '' }); setPage(1); }} className="btn btn-sm ml-2 bg-gray-200 dark:bg-gray-700 dark:text-gray-200">Limpiar</button>
        </div>
      </div>

      {/* Tabla de pacientes */}
      <div className="card">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Apellidos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Teléfono
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha Nacimiento
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Género
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fecha Alta
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  RGPD
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Fuente Captación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Observaciones
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Método Contacto Preferido
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
              {/* Fila para agregar nuevo paciente */}
              {isAdding && (
                <tr className="bg-blue-50 dark:bg-blue-900/20">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    Auto
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newPatient.nombre}
                      onChange={(e) => handleInputChange('nombre', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Nombre *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newPatient.apellidos}
                      onChange={(e) => handleInputChange('apellidos', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Apellidos *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="tel"
                      value={newPatient.telefono}
                      onChange={(e) => handleInputChange('telefono', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Teléfono"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="email"
                      value={newPatient.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Email *"
                      required
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                      Activo
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="date"
                      value={newPatient.fecha_nacimiento}
                      onChange={(e) => handleInputChange('fecha_nacimiento', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={newPatient.genero}
                      onChange={(e) => handleInputChange('genero', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                    >
                      <option value="">Seleccionar</option>
                      <option value="Masculino">Masculino</option>
                      <option value="Femenino">Femenino</option>
                      <option value="Otro">Otro</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {formatDate(newPatient.fecha_alta)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={newPatient.consentimiento_rgpd.toString()}
                      onChange={(e) => handleInputChange('consentimiento_rgpd', e.target.value === 'true')}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                    >
                      <option value="false">No</option>
                      <option value="true">Sí</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <input
                      type="text"
                      value={newPatient.fuente_captacion}
                      onChange={(e) => handleInputChange('fuente_captacion', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Fuente"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      value={newPatient.observaciones}
                      onChange={(e) => handleInputChange('observaciones', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Observaciones"
                    />
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="text"
                      value={newPatient.metodo_contacto_preferido}
                      onChange={(e) => handleInputChange('metodo_contacto_preferido', e.target.value)}
                      className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      placeholder="Método contacto preferido"
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveNew}
                        disabled={createMutation.isPending}
                        className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Guardar"
                      >
                        ✓
                      </button>
                      <button
                        onClick={handleCancelNew}
                        disabled={createMutation.isPending}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                        title="Cancelar"
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {/* Filas de pacientes existentes */}
              {patients?.map((patient: Paciente) => (
                <tr key={patient.id} className={editingPatientId === patient.id ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {patient.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingPatientId === patient.id ? (
                      <input
                        type="text"
                        value={editingPatient.nombre}
                        onChange={(e) => handleEditInputChange('nombre', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Nombre *"
                        required
                      />
                    ) : (
                      patient.nombre
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100">
                    {editingPatientId === patient.id ? (
                      <input
                        type="text"
                        value={editingPatient.apellidos}
                        onChange={(e) => handleEditInputChange('apellidos', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Apellidos *"
                        required
                      />
                    ) : (
                      patient.apellidos || patient.apellido
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="tel"
                        value={editingPatient.telefono}
                        onChange={(e) => handleEditInputChange('telefono', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Teléfono"
                      />
                    ) : (
                      patient.telefono || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="email"
                        value={editingPatient.email}
                        onChange={(e) => handleEditInputChange('email', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Email *"
                        required
                      />
                    ) : (
                      patient.email || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingPatientId === patient.id ? (
                      <select
                        value={editingPatient.estado}
                        onChange={(e) => handleEditInputChange('estado', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="ACTIVO">ACTIVO</option>
                        <option value="INACTIVO">INACTIVO</option>
                      </select>
                    ) : (
                      <StatusSwitch
                        isActive={isPatientActive(patient.estado)}
                        onToggle={() => handleToggleStatus(patient)}
                        loading={updateStatusMutation.isPending}
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="date"
                        value={editingPatient.fecha_nacimiento}
                        onChange={(e) => handleEditInputChange('fecha_nacimiento', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      />
                    ) : (
                      formatDate(patient.fecha_nacimiento)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <select
                        value={editingPatient.genero}
                        onChange={(e) => handleEditInputChange('genero', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="">Seleccionar</option>
                        <option value="Masculino">Masculino</option>
                        <option value="Femenino">Femenino</option>
                        <option value="Otro">Otro</option>
                      </select>
                    ) : (
                      patient.genero || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="date"
                        value={editingPatient.fecha_alta}
                        onChange={(e) => handleEditInputChange('fecha_alta', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      />
                    ) : (
                      formatDate(patient.fecha_alta)
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingPatientId === patient.id ? (
                      <select
                        value={editingPatient.consentimiento_rgpd.toString()}
                        onChange={(e) => handleEditInputChange('consentimiento_rgpd', e.target.value === 'true')}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                      >
                        <option value="false">No</option>
                        <option value="true">Sí</option>
                      </select>
                    ) : (
                      patient.consentimiento_rgpd !== undefined ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          patient.consentimiento_rgpd 
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                        }`}>
                          {patient.consentimiento_rgpd ? 'Sí' : 'No'}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="text"
                        value={editingPatient.fuente_captacion}
                        onChange={(e) => handleEditInputChange('fuente_captacion', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Fuente"
                      />
                    ) : (
                      patient.fuente_captacion || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="text"
                        value={editingPatient.observaciones}
                        onChange={(e) => handleEditInputChange('observaciones', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Observaciones"
                      />
                    ) : (
                      <span className="max-w-xs truncate block">{patient.observaciones || '-'}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {editingPatientId === patient.id ? (
                      <input
                        type="text"
                        value={editingPatient.metodo_contacto_preferido}
                        onChange={(e) => handleEditInputChange('metodo_contacto_preferido', e.target.value)}
                        className="w-full px-2 py-1 text-sm border rounded dark:bg-gray-800 dark:border-gray-600"
                        placeholder="Método contacto preferido"
                      />
                    ) : (
                      patient.metodo_contacto_preferido || '-'
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      {editingPatientId === patient.id ? (
                        <>
                          <button
                            onClick={handleSaveEdit}
                            disabled={updateMutation.isPending}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            title="Guardar"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            disabled={updateMutation.isPending}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50"
                            title="Cancelar"
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleEdit(patient)}
                          disabled={isAdding || editingPatientId !== null}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-50"
                          title="Editar"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(!patients || patients.length === 0) && !isAdding && (
          <div className="text-center py-12">
            <p className="text-gray-500 dark:text-gray-400">
              {t('common.noData')}
            </p>
          </div>
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-gray-600 dark:text-gray-400">
          Mostrando {patients.length} de {total} pacientes
        </div>
        <div className="flex items-center gap-2">
          <button disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))} className="btn btn-xs">Anterior</button>
          <span className="text-sm">Página {page}</span>
          <button disabled={page * limit >= total} onClick={() => setPage(p => p + 1)} className="btn btn-xs">Siguiente</button>
          <select value={limit} onChange={e => { setLimit(Number(e.target.value)); setPage(1); }} className="input input-xs ml-2 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
            {[5, 10, 20, 50].map(opt => <option key={opt} value={opt}>{opt} por página</option>)}
          </select>
        </div>
      </div>
    </div>
  );
} 